import React from 'react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from "../../contexts/authContext";
import { db } from "../../firebase/firebase";
import { doc, updateDoc, getDocs, collection, query, where } from "firebase/firestore";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";


export default function TicketDetails() {
    const location = useLocation();
    const { currentUser, userData } = useAuth();
    const [ticket, setTicket] = useState(location.state?.ticket || null);
    const [isClosing, setIsClosing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    

    useEffect(() => {
        if (ticket?.ticketNumber) {
            fetchUpdatedTicket(ticket.ticketNumber);
        }
    }, [isUpdating]);

    const fetchUpdatedTicket = async (ticketNumber) => {
        try {
            const ticketsRef = collection(db, "tickets");
            const q = query(ticketsRef, where("ticketNumber", "==", ticketNumber));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const updatedTicket = querySnapshot.docs[0].data();
                console.log("Updated Ticket Data:", updatedTicket);
                
                
                setTicket(updatedTicket); // Update state with new ticket details
            }
        } catch (error) {
            console.error("Error fetching updated ticket:", error);
        }
    };


    const reverseFormatDateTime = (formattedDate) => {
        if (!formattedDate) return "";
      
        // Split date and time
        const [datePart, timePart] = formattedDate.split(", ");
        const [day, month, year] = datePart.split("/");
        let [hour, minute, second] = timePart.split(":");
      
        return `${day}${month}${year}_${hour}${minute}${second}`;
      };

    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
      
        const day = dateString.substring(0, 2);
        const month = dateString.substring(2, 4);
        const year = dateString.substring(4, 8);
        const hours = dateString.substring(9, 11);
        const minutes = dateString.substring(11, 13);
        const seconds = dateString.substring(13, 15);
      
        const dateObj = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
      
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(dateObj);
      };

      const handleCloseTicket = async () => {
        if (!ticket || !ticket.ticketNumber) {
            alert("Invalid ticket data.");
            return;
        }

        setIsClosing(true);

        try {
            // Query Firestore to find the document with the matching ticket number
            const ticketsRef = collection(db, "tickets");
            const q = query(ticketsRef, where("ticketNumber", "==", ticket.ticketNumber));
            const querySnapshot = await getDocs(q);
            console.log("Query Snapshot:", querySnapshot);
            

            if (querySnapshot.empty) {
                alert("Ticket not found in Firestore.");
                setIsClosing(false);
                return;
            }

            // Get the first matching document
            const ticketDoc = querySnapshot.docs[0];
            console.log("Ticket Document:", ticketDoc);
            
            const ticketRef = doc(db, "tickets", ticketDoc.id);

            // Update Firestore document
            await updateDoc(ticketRef, {
                status: "closed",
                statusUpdateDate: reverseFormatDateTime(new Date().toLocaleString()),
            });
            

            setSnackbar({ open: true, message: "Ticket closed successfully", severity: "success" });
           
            // alert("Ticket closed successfully.");
            setIsUpdating(true);
        } catch (error) {
            console.error("Error closing ticket:", error);
            alert("Failed to close ticket.");
        }

        setIsClosing(false);
    };
    if (!ticket) {
      return <div className="p-6">No ticket data found.</div>;
    }
  
    return (
        <div className="p-6 px-10 md:px-[20%]">
          <h1 className="text-2xl font-bold pt-14 text-blue-600">Ticket Details</h1>
    
          <div className="mt-4 space-y-4">
            <DetailField label="Ticket No." value={ticket.ticketNumber} />
            <DetailField label="Asset Location" value={ticket.location} />
            <DetailField label="Created Date Time" value={formatDateTime(ticket.date)} />
            <DetailField label="Asset Address" value={ticket.address} />
            <DetailField label="Category" value={ticket.ticketType} />
            <DetailField label="SubCategory" value={ticket.subCategory} />
            <DetailField label="Description" value={ticket.description} />
            <DetailField label="Uploaded Image" value={ticket.image} isImage={true} />
            <DetailField label="Assignee" value={userData.workEmail} />
            <DetailField label="Response" value={ticket.response} />
            <DetailField label="Closing Date" value={formatDateTime(ticket.statusUpdateDate)} />
    
            {/* Display uploaded image if available */}
            {ticket.uploadedImage && (
            <div>
              <p className="font-semibold">Uploaded Image</p>
              <img 
                src={`data:image/png;base64,${ticket.uploadedImage}`} 
                alt="Uploaded Ticket" 
                className="mt-2 w-full max-w-xs border rounded" 
               
              />
            </div>
          )}

           {/* Close Ticket Button */}
           {ticket.status !== "closed" && (
                    <button 
                        className="w-full bg-blue-600 text-white py-2 mt-6 rounded-lg text-lg font-semibold"
                        onClick={handleCloseTicket}
                    >
                        Close Ticket
                    </button>
                )}
          </div>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
      );
  }
  
  // Reusable component for displaying fields
  const DetailField = ({ label, value, isImage }) => (
    <div>
      <p className="font-semibold">{label}</p>
      {isImage && value ? (
        <img 
          src={`data:image/png;base64,${value}`} 
          alt={label} 
          className="mt-2 w-full max-w-xs border rounded" 
        />
      ) : (
        <div className="p-2 border rounded bg-gray-100">{value || "N/A"}</div>
      )}
    </div>
  );